import React from 'react'
import { Box, List, Text } from '@chakra-ui/react'
import {
  Droppable,
  DroppableProvided,
  DroppableStateSnapshot,
} from 'react-beautiful-dnd'
import { AclEnum } from '~core/global.type'
import Action from './Action'
import { OnHoldDelayInput } from './components'
import { TapIcon } from '~editor/components/icons'
import { useUser } from '~editor/hooks/useUser'
import useDispatch from '~editor/hooks/useDispatch'
import { ActionGestureTypes, AnyActionType, getMeta } from '~core/forge.type'

interface IProps {
  activeActionGesture: ActionGestureTypes
  actions: AnyActionType[]
}

function ActionsList(props: IProps) {
  const { actions = [], activeActionGesture } = props
  const hasActions = !!(actions && actions.length)
  const user = useUser()
  const dispatch = useDispatch()

  return (
    <Box
      flex="1"
      display="flex"
      position="relative"
      alignItems={hasActions ? 'initial' : 'center'}
      justifyContent={hasActions ? 'initial' : 'center'}
      flexDirection="column"
    >
      {hasActions ? (
        <>
          {activeActionGesture === ActionGestureTypes.OnHold && (
            <OnHoldDelayInput activeActionGesture={activeActionGesture} />
          )}

          <Box position="relative" flex="1">
            <Droppable droppableId={'actions-droppable'}>
              {(
                dropProvided: DroppableProvided,
                dropSnapshot: DroppableStateSnapshot,
              ) => (
                <List
                  {...dropProvided.droppableProps}
                  flex="1"
                  overflow="auto"
                  minHeight="50px"
                  position="absolute"
                  top="0"
                  left="0"
                  bottom="0"
                  right="0"
                  padding="10px"
                  ref={dropProvided.innerRef}
                >
                  {(actions || []).map(
                    (action: AnyActionType, index: number) => {
                      if (!action) {
                        dispatch.nodes.updateActions({
                          actionGestureType: activeActionGesture,
                          actions: actions.filter((a) => a),
                        })

                        return null
                      }

                      const Meta = getMeta(action)
                      if (
                        Meta.hasOwnProperty('acl') &&
                        ((Meta.acl.includes(AclEnum.STAFF) &&
                          !user?.is_staff) ||
                          (Meta.acl.includes(AclEnum.GLASSPASS) &&
                            !user?.glasspass))
                      ) {
                        return null
                      }
                      return (
                        <Action
                          key={action.id}
                          actions={actions}
                          action={action}
                          index={index}
                          activeActionGesture={activeActionGesture}
                        />
                      )
                    },
                  )}
                  {dropProvided.placeholder}
                </List>
              )}
            </Droppable>
          </Box>
        </>
      ) : (
        <Box textAlign="center" maxWidth="100%" width="436px">
          <TapIcon width="71px" height="71px" color="gameglass.50" />
          <Text
            fontWeight="700"
            fontSize="26px"
            marginTop="42px"
            marginBottom="14px"
          >
            {'Actions Editor'}
          </Text>
          <Text color="ui.text.grey" fontSize="14px">
            {
              'Set up keybinds, mouse clicks and various other events that execute when you press your currently selected element.'
            }
          </Text>
        </Box>
      )}
    </Box>
  )
}

export default ActionsList
